/**
 * SubjectFilterHeader — DRY template header for subject-scoped primitive views (DRR-141).
 *
 * Renders the three-tab filter bar (All / My {primitive} / {Subject label}),
 * the subject dropdown with lifecycle phase badges, and the collapsible
 * constellation context panel for the selected subject.
 *
 * ## What belongs here
 * - Subject scope selector (All / My {X} / {SubjectLabel})
 * - Subject dropdown + lifecycle phase badge
 * - Constellation context panel
 *
 * ## What does NOT belong here
 * - Group By controls — those live in TabulatorPrimitiveManager's toolbar
 * - Column filter controls — served by Tabulator's built-in header filter row
 *
 * ## Usage
 *
 * ```tsx
 * const scope = useSubjectScope({ nameResolver: resolveActorName });
 *
 * <SubjectFilterHeader
 *   scope={scope}
 *   actorName={actorName}
 *   actorLoading={actorLoading}
 *   primitiveLabel="Tasks"    // label for "My Tasks" tab
 * />
 * ```
 *
 * @see useSubjectScope — companion hook providing all state and data
 */

import { useMemo } from 'react';
import {
  SegmentedControl,
  Group,
  Text,
  Badge,
  Loader,
  Select,
  Collapse,
  Paper,
  ActionIcon,
  Tooltip,
  Stack,
} from '@mantine/core';
import { IconChartDots3, IconChevronUp } from '@tabler/icons-react';
import { ConstellationView, type ConstellationNode, type ConstellationEdge } from './ConstellationView';
import type { UseSubjectScopeReturn, SubjectScopeFilter } from './hooks/useSubjectScope';
import { LIFECYCLE_PHASE_COLORS } from './hooks/useSubjectScope';

// ---------------------------------------------------------------------------
// Internal helper
// ---------------------------------------------------------------------------

function roleToRing(role: string): number {
  if (role === 'center') return 0;
  if (role === 'organization') return 1;
  if (role === 'regulatory') return 4;
  return 2;
}

function toCareTeamData(
  constellation: UseSubjectScopeReturn['constellation'],
): { center: ConstellationNode; nodes: ConstellationNode[]; edges: ConstellationEdge[] } | null {
  if (!constellation) return null;

  const nodes: ConstellationNode[] = constellation.nodes.map((n) => ({
    nodeId: n.nodeId,
    name: n.name,
    role: n.role,
    ring: roleToRing(n.role),
    metadata: n.metadata,
  }));

  const edges: ConstellationEdge[] = constellation.edges.map((e) => ({
    sourceId: e.sourceId,
    targetId: e.targetId,
    edgeType: e.edgeType,
    label: e.label,
  }));

  const center = nodes.find((n) => n.ring === 0) ?? {
    nodeId: constellation.center.nodeId,
    name: constellation.center.name,
    role: 'center',
    ring: 0,
  };

  return { center, nodes, edges };
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface SubjectFilterHeaderProps {
  /** Output of useSubjectScope() — all state and data. */
  readonly scope: UseSubjectScopeReturn;
  /** Display name of the current user's actor (for "My {primitive}" badge). */
  readonly actorName?: string;
  /** Whether the actor is still loading. */
  readonly actorLoading?: boolean;
  /**
   * Label for the second tab, e.g., "Tasks", "Instances", "Documents".
   * Renders as "My Tasks", "My Instances", etc.
   * Defaults to "Items".
   */
  readonly primitiveLabel?: string;
  /**
   * Height of the constellation panel in pixels.
   * @default 350
   */
  readonly constellationHeight?: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SubjectFilterHeader({
  scope,
  actorName,
  actorLoading = false,
  primitiveLabel = 'Items',
  constellationHeight = 350,
}: Readonly<SubjectFilterHeaderProps>) {
  const {
    filter,
    setFilter,
    selectedSubjectId,
    setSelectedSubjectId,
    subjectOptions,
    subjectsLoading,
    subjectLabel,
    selectedSubjectName,
    selectedSubjectPhase,
    constellation,
    constellationLoading,
    constellationOpen,
    toggleConstellation,
  } = scope;

  const careTeamData = useMemo(() => toCareTeamData(constellation), [constellation]);

  const selectData = useMemo(
    () => subjectOptions.map((o) => ({ value: o.value, label: o.label })),
    [subjectOptions],
  );

  const segmentedData: { label: string; value: SubjectScopeFilter }[] = [
    { label: 'All', value: 'all' },
    { label: `My ${primitiveLabel}`, value: 'my' },
    { label: subjectLabel, value: 'subject' },
  ];

  // "My {primitive}" badge content — extracted to avoid nested ternary
  let actorBadge: React.ReactNode = null;
  if (actorLoading) {
    actorBadge = <Loader size="xs" />;
  } else if (actorName) {
    actorBadge = <Badge variant="light" color="blue" size="sm">{actorName}</Badge>;
  } else {
    actorBadge = <Text size="xs" c="dimmed">No actor found for current user</Text>;
  }

  // Constellation panel content — extracted to avoid nested ternary
  let constellationContent: React.ReactNode;
  if (constellationLoading) {
    constellationContent = (
      <Group justify="center" py="md">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">Loading care team...</Text>
      </Group>
    );
  } else if (careTeamData) {
    constellationContent = (
      <ConstellationView
        center={careTeamData.center}
        nodes={careTeamData.nodes}
        edges={careTeamData.edges}
        height={constellationHeight}
      />
    );
  } else {
    constellationContent = (
      <Text size="sm" c="dimmed" ta="center" py="md">
        No care team data available for this {subjectLabel.toLowerCase()}
      </Text>
    );
  }

  return (
    <Stack gap="sm" mb="sm">
      <Group gap="md">
        <SegmentedControl
          size="xs"
          value={filter}
          onChange={(v) => setFilter(v as SubjectScopeFilter)}
          data={segmentedData}
        />

        {/* "My {primitive}" badge — show current user's name */}
        {filter === 'my' && <Group gap="xs">{actorBadge}</Group>}

        {/* Subject dropdown */}
        {filter === 'subject' && (
          <Select
            size="xs"
            placeholder={
              subjectsLoading
                ? `Loading ${subjectLabel.toLowerCase()}s...`
                : `Select ${subjectLabel.toLowerCase()}...`
            }
            data={selectData}
            value={selectedSubjectId}
            onChange={setSelectedSubjectId}
            searchable
            clearable
            disabled={subjectsLoading}
            nothingFoundMessage={
              subjectsLoading ? 'Loading...' : `No ${subjectLabel.toLowerCase()}s found`
            }
            w={220}
          />
        )}

        {/* Selected subject badge + lifecycle phase + constellation toggle */}
        {filter === 'subject' && selectedSubjectId && (
          <>
            <Badge variant="light" color="violet" size="sm">
              {selectedSubjectName}
            </Badge>
            {selectedSubjectPhase && (
              <Badge
                variant="light"
                color={LIFECYCLE_PHASE_COLORS[selectedSubjectPhase] ?? 'gray'}
                size="sm"
              >
                {selectedSubjectPhase.charAt(0).toUpperCase() + selectedSubjectPhase.slice(1)}
              </Badge>
            )}
            <Tooltip
              label={constellationOpen ? 'Hide care team' : 'Show care team constellation'}
            >
              <ActionIcon variant="subtle" size="sm" onClick={toggleConstellation}>
                {constellationOpen ? <IconChevronUp size={14} /> : <IconChartDots3 size={14} />}
              </ActionIcon>
            </Tooltip>
          </>
        )}
      </Group>

      {/* Constellation context panel — collapses when hidden */}
      {filter === 'subject' && selectedSubjectId && (
        <Collapse in={constellationOpen}>
          <Paper withBorder p="xs">
            {constellationContent}
          </Paper>
        </Collapse>
      )}
    </Stack>
  );
}
